<script lang="ts">
  import { onMount } from "svelte";
  import { isUserRoleADM } from "../../app.store";
  import ActivityTypeDropdown from "../../components/Business/ActivityTypeDropdown.svelte";
  import CustomerDropdown from "../../components/Business/CustomerDropdown.svelte";
  import DossierDropdown from "../../components/Business/DossierDropdown.svelte";
  import PortalUserDropdown from "../../components/Business/PortalUserDropdown.svelte";
  import Button from "../../components/Button.svelte";
  import DateInput from "../../components/Form/Fields/DateInput.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import NumberInput from "../../components/Form/Fields/NumberInput.svelte";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import Textarea from "../../components/Form/Fields/Textarea.svelte";
  import TimeInput from "../../components/Form/Fields/TimeInput.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import Icon from "../../components/Icon.svelte";
  import MainLayout from "../../layout/MainLayout.svelte";
  import type { IDropdownItem } from "../../services/DTO/dropdown-item";
  import { formatDateHuman, parseDate } from "../../utils/date-utils";
  import {
    initTimesheet,
    load,
    timesheet,
    filter,
    prevDay,
    nextDay,
    prevWeek,
    nextWeek,
    activities,
    activity,
    dossier,
    onAddActivity,
    onEditActivity,
    onSaveActivity,
    onSaveAsNewActivity,
    onCancelActivity,
    fetchDossier,
    extraContentVisible,
    fetchAtivityType,
    selection,
    applyUserRevenue,
  } from "./timesheet.store";
  import TimesheetFragment from "./TimesheetFragment.svelte";

  $: canSubmit =
    $activity != null &&
    $activity.activityTypeId != null &&
    ($activity.hours > 0 || $activity.minutes > 0);

  $: hasDateSelection = !!$selection && !!$selection.dateKey;

  $: currentCustomerId = $activity != null ? $activity.customerId : 0;

  $: currentDossierTypeId = $dossier != null ? $dossier.dossierTypeId : 0;

  $: currentSelectionDate = hasDateSelection
    ? formatDateHuman(parseDate($selection.dateKey))
    : "";

  async function onDossierSelect(event: CustomEvent) {
    let item: IDropdownItem = event.detail;
    let _dossier = await fetchDossier(item.oid);
    dossier.set(_dossier);
    if (_dossier != null && _dossier.customerId != $activity.customerId) {
      $activity.customerId = _dossier.customerId;
    }
  }

  async function onActivityTypeSelect(event: CustomEvent) {
    let item: IDropdownItem = event.detail;
    if ($applyUserRevenue == false) {
      let _at = await fetchAtivityType(item.oid);
      if (_at != null && $activity != null) {
        $activity.hourlyRevenue = _at.hourlyRevenue;
      }
    }
  }

  onMount(() => initTimesheet());
</script>

<MainLayout>
  <div class="timesheet-filters">
    <div class="columns">
      <div class="column width-auto-column button-column col-small-padding">
        <Button on:click={prevWeek}>
          <Icon name="fast_rewind" />
        </Button>
      </div>

      <div class="column width-auto-column button-column col-small-padding">
        <Button on:click={prevDay}>
          <Icon name="chevron_left" />
        </Button>
      </div>

      <div class="column is-2 col-small-padding">
        <FormField>
          <DateInput
            name="Data"
            placeholder="data"
            bind:value={$filter.criteria.dateFrom}
          />
        </FormField>
      </div>

      <div class="column width-auto-column button-column col-small-padding">
        <Button on:click={nextDay}>
          <Icon name="chevron_right" />
        </Button>
      </div>

      <div class="column width-auto-column button-column col-small-padding">
        <Button on:click={nextWeek}>
          <Icon name="fast_forward" />
        </Button>
      </div>

      <div class="column">
        <FormField>
          <CustomerDropdown
            name="Cliente"
            required={false}
            clearVisible={true}
            bind:value={$filter.criteria.customerId}
            on:select={(_) => ($filter.criteria.dossierId = null)}
            on:clear={(_) => ($filter.criteria.dossierId = null)}
          />
        </FormField>
      </div>
      <div class="column">
        <FormField>
          <PortalUserDropdown
            name="Utente"
            required={false}
            disabled={$isUserRoleADM == false}
            clearVisible={true}
            bind:value={$filter.criteria.portalUserId}
          />
        </FormField>
      </div>
      <div class="column is-3 button-column">
        <Button on:click={load}>
          <Icon name="search" />
          <span> Cerca </span>
        </Button>
      </div>
    </div>
  </div>

  <div class="columns">
    <div class="column is-9">
      <div class="box">
        <TimesheetFragment timesheet={$timesheet} />
      </div>
    </div>
    <div class="column is-3">
      <div class="box">
        {#if $activity == null}
          <h1 class="subtitle">Attività {currentSelectionDate}</h1>

          {#if $activities != null && $activities.length > 0}
            {#each $activities as activity}
              <div class="day-activity">
                <Icon
                  customIconStyle="height:1rem;"
                  name="label"
                  on:click={(_) => onEditActivity(activity.oid)}
                />
                <span class="day-activity-description">
                  {activity.activity.hours.toString().padStart(2, "0") +
                    ":" +
                    activity.activity.minutes.toString().padStart(2, "0")}
                  &nbsp; {activity.activityTypeDescription} &nbsp;&ndash;&nbsp;
                  {activity.activity.title}
                </span>
              </div>
            {/each}
          {:else}
            <div>Non ci sono attività</div>
          {/if}

          {#if hasDateSelection}
            <div class="activity-controls has-text-centered">
              <Button on:click={onAddActivity}>
                <Icon name="add" />
                <span>Nuova attività</span>
              </Button>
            </div>
          {/if}
        {:else}
          <FormField>
            <DateInput
              name="Data"
              placeholder="data"
              small={true}
              bind:value={$activity.date}
            />
          </FormField>
          <FormField>
            <CustomerDropdown
              name="Cliente"
              small={true}
              required={false}
              bind:value={$activity.customerId}
              on:select={($activity.dossierId = null)}
            />
          </FormField>
          <FormField>
            <ActivityTypeDropdown
              name="Causale Attività"
              required={true}
              small={true}
              dossierTypeId={currentDossierTypeId}
              bind:value={$activity.activityTypeId}
              on:select={onActivityTypeSelect}
            />
          </FormField>
          <FormField>
            <Input
              name="Descrizione aggiuntiva"
              placeholder="descrizione aggiuntiva"
              small={true}
              required={false}
              bind:value={$activity.title}
            />
          </FormField>
          <FormField>
            <DossierDropdown
              name="Pratica"
              required={false}
              small={true}
              customerId={currentCustomerId}
              bind:value={$activity.dossierId}
              on:select={onDossierSelect}
            />
          </FormField>
          <!--
          <FormField>
            <PortalUserDropdown
              name="Utente"
              required={true}
              small={true}
              bind:value={$activity.portalUserId}
            />
          </FormField>
          -->

          <FormField klass="has-text-centered">
            <TimeInput
              name="Tempo (Ore : Minuti)"
              required={true}
              small={true}
              bind:hours={$activity.hours}
              bind:minutes={$activity.minutes}
            />
          </FormField>

          <div class="extra-content">
            <button
              class="button expand-button"
              on:click={(_) => ($extraContentVisible = !$extraContentVisible)}
            >
              <span class="material-icons hoverable">
                {#if $extraContentVisible}
                  keyboard_arrow_up
                {:else}
                  keyboard_arrow_down
                {/if}
              </span>
            </button>
          </div>

          {#if $extraContentVisible}
            <div class="columns">
              <div class="column">
                <FormField>
                  <NumberInput
                    name="Ricavo orario"
                    placeholder="ricavo orario"
                    required={true}
                    small={true}
                    bind:value={$activity.hourlyRevenue}
                  />
                </FormField>
              </div>
              <div class="column">
                {#if $isUserRoleADM}
                  <FormField>
                    <NumberInput
                      name="Costo orario"
                      placeholder="costo orario"
                      required={true}
                      small={true}
                      bind:value={$activity.hourlyLaborCost}
                    />
                  </FormField>
                {/if}
              </div>
            </div>

            <div class="columns">
              <div class="column is-6">
                <FormField>
                  <NumberInput
                    name="Costi anticipati"
                    placeholder="costi anticipati"
                    disabled={$activity.costs == 0}
                    required={false}
                    small={true}
                    bind:value={$activity.costsAmount}
                  />
                </FormField>
              </div>
              <div class="column is-3">
                <FormField klass="has-text-centered">
                  <Switch
                    name="Costi"
                    condensed={true}
                    bind:value={$activity.costs}
                    on:toggle={(_) => ($activity.costsAmount = 0)}
                  />
                </FormField>
              </div>
              <div class="column is-3">
                <FormField klass="has-text-centered">
                  <Switch
                    name="Fatturata"
                    condensed={true}
                    bind:value={$activity.billed}
                  />
                </FormField>
              </div>
            </div>
            <FormField>
              <Textarea
                name="Note"
                placeholder="note"
                required={false}
                small={true}
                rows={2}
                bind:value={$activity.notes}
              />
            </FormField>
          {/if}

          <div class="activity-controls has-text-centered">
            <Button
              klass="is-primary"
              disabled={!canSubmit}
              on:click={onSaveActivity}>Salva</Button
            >
            {#if $activity.oid != 0}
              <Button
                klass="is-primary"
                disabled={!canSubmit}
                on:click={onSaveAsNewActivity}>Duplica</Button
              >
            {/if}
            <Button klass="is-light" on:click={onCancelActivity}>Annulla</Button
            >
          </div>
        {/if}
      </div>
    </div>
  </div>
</MainLayout>

<style>
  .timesheet-filters {
    margin-bottom: 0.5rem;
  }

  .button-column {
    margin-top: auto;
  }

  .width-auto-column {
    width: auto;
    flex-grow: 0;
  }

  .columns .col-small-padding:first-child {
    padding-left: 0.75rem;
  }
  .columns .col-small-padding:last-child {
    padding-right: 0.75rem;
  }

  .col-small-padding {
    padding: 0.75rem 0.25rem;
  }

  .activity-controls {
    margin-top: 1rem;
  }

  .extra-content {
    height: 1px;
    width: 100%;
    background: #eee;
    position: relative;
    text-align: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .extra-content .expand-button {
    top: -0.5rem;
    border-radius: 1rem;
    width: 1rem;
    height: 1rem;
  }

  .day-activity {
    display: flex;
  }

  .day-activity-description {
    margin-left: 5px;
    line-height: 1;
    margin-bottom: 0.2rem;
  }
</style>
